import { FabricText, Group, loadSVGFromString, util } from 'fabric';
import EventPublisher from '../event_publisher';
import {
  FONT_FAMILY,
  FONT_SIZE,
  FONT_WEIGHT,
  CELL_SIZE,
  TOP_PADDING,
  LEFT_PADDING
} from './render_properties';

export default class LandmarkRenderer {
  constructor(landmark, readOnly = false) {
    this.landmark = landmark;
    this.readOnly = readOnly;
    this.topPadding = readOnly ? TOP_PADDING : 0;
    this.leftPadding = readOnly ? LEFT_PADDING : 0;
  }

  setCanvas(canvas) {
    this.canvas = canvas;
    return this;
  }

  render() {
    this.renderSVG();
  }

  async renderSVG() {
    const response = await fetch(this.landmark.svgURL);
    const { objects, options } = await loadSVGFromString(await response.text());
    const obj = util.groupSVGElements(objects, options);
    this.svg = obj;
    const { width, height } = obj;
    const scaleX = (this.landmark.width * CELL_SIZE) / width;
    const scaleY = (this.landmark.height * CELL_SIZE) / height;
    const svgWidth = obj.width * scaleX;
    const svgHeight = obj.height * scaleY;

    this.label = this.renderLabel(this.landmark.label);
    this.svg.set({ left: 0, top: 0, originX: 'center', originY: 'center' });
    this.label.set({ left: 0, top: 0, originX: 'center', originY: 'center', strokewidth: 0 });
    obj.set({ scaleX: scaleX, scaleY: scaleY, originX: 'center', originY: 'center', opacity: 0.8 });

    const groupLeft = (this.landmark.topLeftX * CELL_SIZE) + this.leftPadding + svgWidth / 2;
    const groupTop = (this.landmark.topLeftY * CELL_SIZE) + this.topPadding + svgHeight / 2;

    this.renderedElement = new Group([this.svg, this.label], {
      left: groupLeft,
      top: groupTop,
      angle: this.landmark.rotation,
      originX: 'center',
      originY: 'center',
      lockMovementX: this.readOnly, lockMovementY: this.readOnly,
      interactive: true,
    });
    this.setupEventListeners()
    this.canvas.add(this.renderedElement);
    this.canvas.sendObjectToBack(this.renderedElement);
  }

  setupEventListeners() {
    this.renderedElement.on('selected', this.onSelected);
    this.renderedElement.on('moving', this.onMoving);
    this.renderedElement.on('scaling', this.onMoving);
    this.renderedElement.on('modified', this.onModified);
    this.renderedElement.on('selection:moved', this.onSelectionMoved);
    this.renderedElement.on('selection:created', this.onSelectionCreated);
  }

  onModified = event => {
    switch (event.action) {
    case 'drag':
      this.onMoved(event)
      break
    case 'scale':
    case 'scaleX':
    case 'scaleY':
      this.onScaled(event)
      break
    case 'rotate':
      this.onRotated(event)
    }
  }

  getActualLeft() {
    return this.renderedElement.getCenterPoint().x - (this.svg.width * this.svg.scaleX / 2);
  }

  getActualTop() {
    return this.renderedElement.getCenterPoint().y - (this.svg.height * this.svg.scaleY / 2);
  }

  onMoving = () => {
    const goodLeft = this.getActualLeft();
    const goodTop = this.getActualTop();
    this.landmark.topLeftX = goodLeft / CELL_SIZE;
    this.landmark.topLeftY = goodTop / CELL_SIZE;
    EventPublisher.publish('object-moving', { object: this.landmark });
  }

  onRotated = () => {
    const angle = this.renderedElement.angle;
    this.landmark.topLeftX = this.getActualLeft() / CELL_SIZE;
    this.landmark.topLeftY = this.getActualTop() / CELL_SIZE;
    this.landmark.rotation = angle;
  }

  onSelected = () => {
    EventPublisher.publish('landmark-selected', { landmark: this.landmark });
  }

  onSelectionCreated = () => {
    this.initialX = this.landmark.topLeftX;
    this.initialY = this.landmark.topLeftY;
  }

  onSelectionMoved = event => {
    const { leftOffset, topOffset } = event;
    this.landmark.topLeftX = (leftOffset / CELL_SIZE) + this.initialX;
    this.landmark.topLeftY = (topOffset / CELL_SIZE) + this.initialY;
    EventPublisher.publish('object-moved', { object: this.landmark });
  }

  onMoved = () => {
    EventPublisher.publish('object-moved', { object: this.landmark });
  }

  onScaled = () => {
    const { width, height, scaleX, scaleY } = this.renderedElement;
    this.landmark.topLeftX = this.getActualLeft() / CELL_SIZE;
    this.landmark.topLeftY = this.getActualTop() / CELL_SIZE;
    this.landmark.width = scaleX * width / CELL_SIZE;
    this.landmark.height = scaleY * height / CELL_SIZE;
  }

  setLabel(label) {
    this.label.set({ text: label });
    this.canvas.requestRenderAll();
    this.canvas.fire('object:modified');
  }

  renderLabel(label = '') {
    return new FabricText(label, {
      fontSize: FONT_SIZE,
      fontFamily: FONT_FAMILY,
      fontWeight: FONT_WEIGHT,
      selectable: false,
      originX: 'center',
      originY: 'center',
      angle: -this.landmark.rotation,
    });
  }

  setActive() {
    if (!this.renderedElement) return;
    if (this.canvas.getActiveObject().type === 'activeSelection') return;
    this.canvas.setActiveObject(this.renderedElement);
    this.canvas.requestRenderAll();
  }

  remove() {
    this.canvas.remove(this.renderedElement);
  }
}
